/**
 * Contact Section Component
 * Contact form with validation and social links
 */
import { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SendIcon from '@mui/icons-material/Send';
 import emailjs from 'emailjs-com';

const Contact = () => {
  // Intersection observer for scroll animations
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Contact info data
  const contactInfo = [
    { icon: EmailIcon, label: 'Email', value: 'riddhipatel2525@gmail.com' },
    { icon: PhoneIcon, label: 'Phone', value: '+1 (647) 331-4067' },
    { icon: LocationOnIcon, label: 'Location', value: 'Cambridge, ON. Canada' },
  ];

  // Social links data
  const socialLinks = [
    { icon: GitHubIcon, url: 'https://github.com/riddhipatel-design', label: 'GitHub' },
    { icon: LinkedInIcon, url: 'https://www.linkedin.com/in/riddhi-patel-d41849201/', label: 'LinkedIn' },
   
  ];

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle form submission


const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsSubmitting(true);
  setSubmitStatus(null);

  try {
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      {
        title: 'Portfolio Contact Form',
        name: formData.name,
        email: formData.email,
        message: formData.message,
      },
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );

    setSubmitStatus('success');
    setFormData({ name: '', email: '', message: '' });
  } catch (error) {
    console.error('EmailJS error:', error);
    setSubmitStatus('error');
  } finally {
    setIsSubmitting(false);
  }
};



  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section id="contact" className="contact-section" ref={ref}>
      <Container>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">Let us work together</p>
        </motion.div>

        <Row>
          {/* Contact Info */}
          <Col lg={5} className="mb-5 mb-lg-0">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <motion.h3 
                variants={itemVariants}
                style={{ marginBottom: '1.5rem', fontWeight: 600 }}
              >
                Contact Information
              </motion.h3>

              <motion.p 
                variants={itemVariants}
                style={{ color: 'var(--gray-light)', marginBottom: '2rem' }}
              >
                Feel free to reach out! I am always open to discussing new projects, 
                creative ideas, or opportunities to be part of your vision.
              </motion.p>

              {/* Contact Details */}
              <div className="contact-info">
                {contactInfo.map((info, index) => (
                  <motion.div 
                    key={index} 
                    variants={itemVariants}
                    className="contact-item"
                  >
                    <div className="contact-icon">
                      <info.icon />
                    </div>
                    <div>
                      <strong>{info.label}</strong>
                      <p style={{ margin: 0 }}>{info.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Links */}
              <motion.div variants={itemVariants}>
                <h4 style={{ marginBottom: '1rem', fontWeight: 600 }}>Follow Me</h4>
                <div className="social-links">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label={social.label}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <social.icon />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </Col>

          {/* Contact Form */}
          <Col lg={7}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="contact-form">
                <h3 style={{ marginBottom: '1.5rem', fontWeight: 600 }}>
                  Send Me a Message
                </h3>

                {/* Status Alerts */}
                {submitStatus === 'success' && (
                  <Alert variant="success" className="mb-4">
                    Message sent successfully! I will get back to you soon.
                  </Alert>
                )}
                {submitStatus === 'error' && (
                  <Alert variant="danger" className="mb-4">
                    Failed to send message. Please try again or email directly.
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                  {/* Name Field */}
                  <Form.Group className="mb-4">
                    <Form.Label>Your Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Name"
                      className="form-control-custom"
                      isInvalid={!!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Email Field */}
                  <Form.Group className="mb-4">
                    <Form.Label>Your Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="abc@example.com"
                      className="form-control-custom"
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Message Field */}
                  <Form.Group className="mb-4">
                    <Form.Label>Your Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell me about your project..."
                      className="form-control-custom"
                      isInvalid={!!errors.message}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Submit Button */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      type="submit"
                      className="btn-primary-custom w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <SendIcon sx={{ mr: 1 }} />
                          Send Message
                        </>
                      )}
                    </Button>
                  </motion.div>
                </Form>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;
